import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  CommandStatus,
  DeviceConnectionQuery,
  DeviceConnectionQueryDataOut, DownloadDeviceConnectionDataIn,
  PageInfo,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { AuthService } from '../../../services/auth.service';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRService } from '../../../services/signalr.service';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { DeviceConnectionDetailsComponent } from './device-connection-details/device-connection-details.component';
import { FileService } from "../../../services/file.service";
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { processDateRange } from '../../../helper/date-utils';
import { generateFileNameAppendix } from '../../../helper/download-filename';

@Component({
  selector: 'app-device-connection',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    AngularSvgIconModule,
    DatepickerComponent,
    SortComponent,
    SearchComponent,
    PaginationComponent,
    IsNewDirective,
    DeviceConnectionDetailsComponent,
  ],
  templateUrl: './device-connection.component.html',
  styleUrl: './device-connection.component.scss',
})
export class DeviceConnectionComponent extends SignalRBaseComponent implements OnInit {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfItems: number = 0;
  public lastPageSize: number = 1;

  isDatepickerOpen = false;
  isModalOpen = false;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  PermissionsEnum = PermissionsEnum;
  public selectedDeviceTime: DeviceConnectionQueryDataOut | undefined;

  public readonly CommandStatus = CommandStatus;
  data: DeviceConnectionQueryDataOut[] = [];
  commandStatusDict: Record<
    string,
    { status: CommandStatus; errorMessage?: string } | undefined
  > = {};

  requestData = new DeviceConnectionQuery({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    public clipboardService: ClipboardService,
    signalRService: SignalRService,
    private fileService: FileService,
  ) {
    super(signalRService);
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.reloadTable(1);
  }

  protected override registerHandlers(): void {
    // Handle Online Status updates
    this.registerSignalREvent('online-status', (message) => {
      const item = this.data.find((i) => i.meterNumber === message.meterNumber);
      if (!item?.meterNumber) return;

      this.commandStatusDict[item.meterNumber] = {
        status: message.commandStatus,
        errorMessage: message.errorMessage,
      };

      if (message.commandStatus === 'Success') {
        item.sessionStatus = message.meterIsOnline;
        item.latestConnection = message.lastResponseTime;
      } else {
        item.sessionStatus = false;
      }
    });

    // Handle Ping Device results
    this.registerSignalREvent('ping-device', (message) => {
      const item = this.data.find((i) => i.meterNumber === message.meterNumber);
      if (!item?.meterNumber) return;

      this.commandStatusDict[item.meterNumber] = {
        status: message.status,
        errorMessage: message.errorMessage,
      };

      if (message.status === 'Success') {
        item.sessionStatus = message.status;
        item.latestConnection = message.pingTimeUtc;
      } else {
        item.sessionStatus = false;
      }
    });
  }

  private async updateSignalRSubscriptions(): Promise<void> {
    if (!this.data || this.data.length === 0) return;

    const deviceNumbers = this.data
      .map(item => item.meterNumber)
      .filter((no): no is string => !!no);

    await this.replaceDeviceSubscriptions(deviceNumbers);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData.pageInfo!.page = this.pageNumber;

    this.selectedDeviceTime = undefined;
    this.client.queryDeviceConnection(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data ?? [];
        this.numberOfItems = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfItems / this.pageSize);

        // Ensure SignalR is listening to the new batch of meters
        this.updateSignalRSubscriptions();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.selectedDeviceTime = undefined;
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  handleRowClick(row: DeviceConnectionQueryDataOut) {
    if (!this.isModalOpen) {
      this.selectedDeviceTime = row;
    }
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  filterDatesInTable() {
    this.client.queryDeviceConnection(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data ?? [];
        this.numberOfItems = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfItems / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  retryCommand(row: DeviceConnectionQueryDataOut, event: MouseEvent) {
    event.stopPropagation();
    if (!row.meterNumber) {
      return; // skip if no meter number
    }

    this.commandStatusDict[row.meterNumber] = { status: CommandStatus.Pending };
    this.client.pingMeter(row.meterNumber).subscribe({
      next: (result) => this.toastr.success(result),
      error: (err) => this.toastr.error(err),
    });
  }

  getErrorMessage(meterNumber?: string): string | null {
    if (!meterNumber) return null;
    return this.commandStatusDict[meterNumber]?.errorMessage ?? null;
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadDeviceConnectionDataIn({
      search: this.requestData.search,
      startDate: this.requestData.startDate,
      endDate: this.requestData.endDate,
      sorting: this.requestData.sorting,
    });

    this.client.downloadDeviceConnectionListFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `${'ConnectionTime'}${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
